import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
// @mui
import Box from "@mui/material/Box";
// api
import { useLazyGetBalanceQuery } from "src/api/report";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
import { useAuthContext } from "src/auth/hooks";
// redux
import { dispatch } from "src/redux/store";
import { getUnreadTaskNum } from "src/redux/slices/task";
//
import Main from "src/layouts/dashboard/main";
import Header from "src/layouts/dashboard/header";
import NavVertical from "src/layouts/dashboard/nav-vertical";
import BannerVerify from "src/layouts/dashboard/banner-verify";

// ----------------------------------------------------------------------

export default function DashboardLayout({ children }) {
    const hasWindow = typeof window !== "undefined";

    const { user, getUser: getUserAuth } = useAuthContext();

    const { pathname } = useLocation();

    const nav = useBoolean();

    const [ getBalance ] = useLazyGetBalanceQuery();

    const timeOutId = useRef();
    const bannerRef = useRef(null);

    const [ width, setWidth ] = useState(hasWindow ? window.innerWidth : null);
    const [ bannerHeight, setBannerHeight ] = useState(40);
    //
    const showBanner = !user?.email_verified_at;

    const renderNavVertical = <NavVertical openNav={ nav.value } onCloseNav={ nav.onFalse } hasPayFailed={ showBanner } bannerHeight={ bannerHeight } />;

    // ----------------------------------

    useEffect(() => {
        getBalance();
    }, [ getBalance, pathname ]);

    useEffect(() => {
        dispatch(getUnreadTaskNum());
    }, [ pathname ]);

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };

        window.addEventListener("resize", () => {
            clearTimeout(timeOutId.current);
            timeOutId.current = setTimeout(handleResize, 500);
        });
    }, []);

    useEffect(() => {
        if (showBanner) getUserAuth();
    }, [ pathname, showBanner, getUserAuth ]);

    useEffect(() => {
        if (bannerRef?.current) {
            setBannerHeight(bannerRef.current?.clientHeight ?? 40);
        }
    }, [ width, bannerRef?.current ]);

    // ----------------------------------

    return (
        <>
            { showBanner && (
                <BannerVerify ref={ bannerRef } />
            ) }

            <Header onOpenNav={ nav.onTrue } hasPayFailed={ showBanner } bannerHeight={ bannerHeight } />

            <Box
                sx={ {
                    display: { lg: "flex" },
                    minHeight: { lg: 1 },
                    pt: showBanner ? `${ bannerHeight }px` : 0,
                    zIndex: theme => theme.zIndex.appBar + 2
                } }
            >
                { renderNavVertical }

                <Main sx={ { height: showBanner ? `calc( 100vh - ${ bannerHeight }px)` : "100vh" } }>{ children }</Main>
            </Box>
        </>
    );
}

DashboardLayout.propTypes = {
    children: PropTypes.node
};
